import React, { useCallback, useMemo } from 'react'
import find from 'lodash/find'
import { Edit as EditIcon } from '@material-ui/icons'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'

import { timezones } from 'civic-champs-shared/helpers/timezones'
import { INTERNAL_ADMIN } from '../../civic-champs-shared/auth/utils/permissions'
import { useCurrentOrg, useHasRole } from 'auth/hooks'
import useSuccessNotification from 'civic-champs-shared/api/hooks/useSuccessNotification'
import useStyles from '../hooks/useStyles'
import { Organization, SubscriptionDetails } from 'organization/types'
import { addressToString } from 'champion/components/ProfileInfoCard'
import { useShowOrganizationEdit } from 'organization/hooks/useShowOrganizationEdit'
import { useShowOrganizationSettingsEdit } from 'organization/hooks/useShowOrganizationSettingsEdit'
import { useFeatureEnabled, useFeatureEnabledForOrg } from 'core/feature/hooks'
import useSubscriptionDetails from 'organization/hooks/useSubscriptionDetails'
import { useUISettings } from 'hooks/useUISettings'
import Alert from '@material-ui/lab/Alert'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import CustomerEditSubscriptionModal from './CustomerEditSubscriptionModal'
import { FeatureType } from 'core/feature/types'
import { useTranslation } from 'react-i18next'
import FeatureEnabled from 'core/feature/components/FeatureEnabled'
import { adminLocaleLabelKey, resolveOrgAdminLocales } from 'i18n/adminLocales'
import { INTERNATIONALIZATION_FEATURE } from 'i18n/feature'

export const SectionHeader = ({ title, onEdit }: { title: string; onEdit: () => void }) => {
  const classes = useStyles()

  return (
    <div className={classes.sectionHeader}>
      <div className={classes.sectionTitle}>{title}</div>
      <EditIcon className={classes.editIcon} onClick={onEdit} />
    </div>
  )
}

export const OrganizationOverview = ({
  organization,
  handleDispatchOrganization,
}: {
  organization?: Organization
  handleDispatchOrganization?: (value: Organization) => void
}) => {
  const currentOrg = useCurrentOrg() as Organization
  const org = organization ?? currentOrg
  const site = useMemo(() => (org.website ? org.website.replace(/^http(s)?:\/\//, '') : null), [org.website])
  const onEditDetails = useShowOrganizationEdit(org, handleDispatchOrganization)
  const onEditSettings = useShowOrganizationSettingsEdit(org, handleDispatchOrganization)
  const isInternalAdmin = useHasRole(INTERNAL_ADMIN)

  // @ts-ignore
  const { showAdminItems } = useUISettings()
  const showSubToCustomer = useFeatureEnabledForOrg(org.id, 'ShowSubscriptionDetailsToCustomer', FeatureType.Product)
  const [subDetails, { edit: editSub, loading: subLoading }] = useSubscriptionDetails(org)
  const showCustomerEdit = useShowPrompt(CustomerEditSubscriptionModal)

  const onEditSub = useCallback(() => {
    if (isInternalAdmin) {
      editSub()
    } else {
      showCustomerEdit({})
    }
  }, [isInternalAdmin, editSub, showCustomerEdit])

  const showSub = !subLoading && ((subDetails && showSubToCustomer) || showAdminItems)

  const volunteerCalendarUrl = useMemo(
    () => `${process.env.REACT_APP_EVENTS_SITE_ENDPOINT}/organizations/${org.id}/calendar/month`,
    [org.id],
  )
  const onCopySuccess = useSuccessNotification(true)
  const handleCopyVolunteerCalendarLink = useCallback(async () => {
    await navigator.clipboard.writeText(volunteerCalendarUrl)
    onCopySuccess('Link copied')
  }, [onCopySuccess, volunteerCalendarUrl])

  const classes = useStyles()
  const { t } = useTranslation(['admin', 'common'])
  const { supportedAdminLocales, defaultAdminLocale } = resolveOrgAdminLocales(org)
  const formatLocale = (locale: string) => t(adminLocaleLabelKey(locale), { ns: 'common' })

  return (
    <div className={classes.details}>
      <div className={classes.info}>
        <SectionHeader title={`Organization Details`} onEdit={onEditDetails} />
        <div className={classes.sectionRow}>
          <div>Name:</div>
          <div>{org.name}</div>
        </div>
        <div className={classes.sectionRow}>
          <div>Timezone:</div>
          <div>{find(timezones, { value: org.timeZone })?.name}</div>
        </div>
        {site && (
          <div className={classes.sectionRow}>
            <div>Website:</div>
            <div>
              <a href={'https://' + site} target="_blank" rel="noopener noreferrer">
                {site}
              </a>
            </div>
          </div>
        )}
      </div>
      <hr className={classes.separator} />
      <div className={classes.info}>
        <SectionHeader title={`Organization Settings`} onEdit={onEditSettings} />
        <div className={classes.sectionRow}>
          <div>Address:</div>
          <div>{addressToString(org.address)}</div>
        </div>
        <div className={classes.sectionRow}>
          <div>Calendar Visibility:</div>
          <div className={classes.calendarVisibilityValue}>
            <span>{org.limitFutureEventVisibility ? `${org.futureEventVisibilityDays} days` : 'All Events'}</span>
            <span className={classes.calendarLinkGroup}>
              <a href={volunteerCalendarUrl} target="_blank" rel="noopener noreferrer" className={classes.calendarLink}>
                View Volunteer Web Calendar
              </a>
              <FileCopyOutlinedIcon className={classes.copyIcon} onClick={handleCopyVolunteerCalendarLink} />
            </span>
          </div>
        </div>
        <div className={classes.sectionRow}>
          <div>Silent Mode:</div>
          <div>{org.sandboxModeEnabled ? 'Enabled' : 'Disabled'}</div>
        </div>
        {isInternalAdmin && (
          <div className={classes.sectionRow}>
            <div>Reply-To Email:</div>
            <div>{org.replyToEmail}</div>
          </div>
        )}
        <FeatureEnabled code={INTERNATIONALIZATION_FEATURE}>
          <div className={classes.sectionRow}>
            <div>{t('admin:organization.supportedAdminLocales')}:</div>
            <div>{supportedAdminLocales.map(formatLocale).join(', ')}</div>
          </div>
          <div className={classes.sectionRow}>
            <div>{t('admin:organization.defaultAdminLocale')}:</div>
            <div>{formatLocale(defaultAdminLocale)}</div>
          </div>
        </FeatureEnabled>
      </div>
      {showSub && (
        <>
          <hr className={classes.separator} />
          <div className={classes.info}>
            <SectionHeader title={`Subscription Information`} onEdit={onEditSub} />
            <div className={classes.sectionRow}>
              <div>Pricing Tier:</div>
              <div>{subDetails?.pricingTier}</div>
            </div>
            <div className={classes.sectionRow}>
              <div>Start Date:</div>
              <div>{subDetails?.startDate}</div>
            </div>
            <div className={classes.sectionRow}>
              <div>End Date:</div>
              <div>{subDetails?.endDate}</div>
            </div>
            <div className={classes.sectionRow}>
              <div>Renewal Status:</div>
              <div>{subDetails?.renewalStatus}</div>
            </div>
            <div className={classes.sectionRow}>
              <div>Renewal Rate:</div>
              <div>{subDetails?.renewalRate}</div>
            </div>
            <div className={classes.sectionRow}>
              <div>Notes:</div>
              <div>{subDetails?.notes}</div>
            </div>
            {showAdminItems && (
              <div className={classes.alert}>
                {!showSubToCustomer || !subDetails ? (
                  <Alert severity="warning">This is not currently visible to the Customer</Alert>
                ) : (
                  <Alert severity="success">This is visible to the Customer</Alert>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
